/**
 * Task React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksService, type TaskListParams, type EntityTaskScope } from '../api/tasks';
import type { CreateTaskDto, UpdateTaskDto, TaskStatus } from '../types/tasks';
import { taskKeys } from './query-keys';

export function useTasks(params?: TaskListParams) {
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: () => tasksService.list(params),
  });
}

export function useTaskSummary() {
  return useQuery({
    queryKey: taskKeys.summary(),
    queryFn: () => tasksService.summary(),
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(id || ''),
    queryFn: () => tasksService.getById(id!),
    enabled: !!id,
  });
}

/** The tasks linked to one business record — backs the entity Tasks rail. */
export function useEntityTasks(scope: EntityTaskScope | undefined) {
  return useQuery({
    queryKey: taskKeys.byEntity(
      scope ?? { pluginId: '', entityType: '', entityId: '' }
    ),
    queryFn: () => tasksService.listForEntity(scope!),
    enabled: !!scope?.entityId,
  });
}

/**
 * The mentionable/assignable users. `searchQuery` is passed to the server so the
 * @mention typeahead stays correct in a company too large to send down whole.
 */
export function useAssignableUsers(searchQuery?: string) {
  return useQuery({
    queryKey: taskKeys.assignableUsers(searchQuery),
    queryFn: () => tasksService.getAssignableUsers(searchQuery),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskDto) => tasksService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskDto }) =>
      tasksService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useUpdateTaskStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus }) =>
      tasksService.updateStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useAddComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      tasksService.addComment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useCloseTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksService.close(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksService.reopen(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}
